import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Box,
	DialogActions,
	Button,
	Autocomplete,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { useParams } from 'react-router-dom';
import {
	attachEmUserRequest,
	createKeycloakUserRequest,
	getEmKeycloakUsersFailure,
	getKeycloakGroupsRequest,
	getKeycloakUsersRequest,
} from '../../../../../../redux/reducers/accessManagement/reducer';
import {
	getEmKeycloakUsers,
	getEmKeycloakUsersLoading,
	getKeycloakUsers,
	getKeycloakUsersLoading,
} from '../../../../../../redux/reducers/accessManagement/selectors';

export type TAddUseDialogProps = {
	open: boolean;
	setClose: () => void;
};

export const AddUseDialog = ({ open, setClose }: TAddUseDialogProps) => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const [email, setEmail] = useState('');
	const [debouncedEmail] = useDebounce(email, 700);
	const [searchEmailError, setSearchEmailError] = useState('');
	const [emailError, setEmailError] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const keycloakUsers = useSelector(getKeycloakUsers);
	const keycloakUsersLoading = useSelector(getKeycloakUsersLoading);
	const emKeycloakUsersLoading = useSelector(getEmKeycloakUsersLoading);

	// Simplified RFC 5322-compliant email regex
	const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

	const reset = () => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setEmailError('');
		setIsAutocompleteOpen(false);
	};

	const validate = () => {
		let isValid = true;
		if (!email) {
			setEmailError('Email is required');
			isValid = false;
		} else if (!emailRegex.test(email.trim())) {
			setEmailError('Email is invalid');
			isValid = false;
		} else setEmailError('');

		if (!firstName) {
			setFirstNameError('First name is required');
			isValid = false;
		} else setFirstNameError('');

		if (!lastName) {
			setLastNameError('Last name is required');
			isValid = false;
		} else setLastNameError('');

		return isValid;
	};

	// useEffect(() => {
	// 	if (!open) return;
	// 	dispatch(getKeycloakUsersRequest({ pageSize: 10, pageNumber: 1 }));
	// }, [dispatch, open]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
	};
	useEffect(() => {
		if (!open) return;
		dispatch(getKeycloakUsersRequest({ pageSize: 10, pageNumber: 1, email: debouncedEmail }));
	}, [debouncedEmail, dispatch, open]);

	useEffect(() => {
		if (!!email && (keycloakUsers?.data?.length === 0 || !keycloakUsers?.data)) {
			setSearchEmailError('User not found. Create new user?');
		} else {
			setSearchEmailError('');
		}
	}, [keycloakUsers, email]);

	const hasUsers = !!keycloakUsers?.data && keycloakUsers.data.length > 0;

	useEffect(() => {
		if (!hasUsers) {
			setIsAutocompleteOpen(false);
		}
	}, [hasUsers]);

	const handleAddUser = () => {
		if (keycloakUsers?.data[0].email) {
			dispatch(
				attachEmUserRequest({
					body: { email: keycloakUsers?.data[0].email, emId: Number(emId) },
					getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
				}),
			);
			reset();
			setClose();
		}
	};

	const handleCreateUser = () => {
		if (!validate()) return;
		dispatch(
			createKeycloakUserRequest({
				body: { email: debouncedEmail, emId: Number(emId), firstName, lastName },
				getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
			}),
		);
		reset();
		setClose();
	};

	const handleClose = () => {
		reset();
		setClose();
	};

	const handleSetEmail = (text: string) => {
		setEmail(text);
	};

	const handleSetFirstName = (text: string) => {
		setFirstName(text);
	};

	const handleSetLastName = (text: string) => {
		setLastName(text);
	};

	useEffect(() => {
		const isValid = validate();
		console.log('======isValid======', isValid);
		console.log('======email======', emailError);
		console.log('======firstName======', firstNameError);
		console.log('======lastName======', lastNameError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [email, firstName, lastName]);

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary" dangerouslySetInnerHTML={{ __html: `Add User` }} />
			<DialogContent
				sx={{
					p: '12px 12px 12px 12px',
				}}
			>
				<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					<Autocomplete
						options={keycloakUsers?.data ?? []}
						loading={keycloakUsersLoading || emKeycloakUsersLoading}
						renderInput={params => (
							<TextField {...params} label="Search User" onChange={handleChange} />
						)}
						getOptionLabel={option => option.email}
						onChange={(e, value) => {
							setEmail(value?.email ?? '');
							setEmailError('');
						}}
						open={isAutocompleteOpen}
						onOpen={() => setIsAutocompleteOpen(hasUsers)}
						onClose={() => setIsAutocompleteOpen(false)}
						fullWidth
					/>

					{!!email && keycloakUsers?.data?.length === 1 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<Typography color="primary">
								Email: <strong>{keycloakUsers?.data[0].email ?? '-'}</strong>
							</Typography>
							<Typography color="primary">
								First Name: <strong>{keycloakUsers?.data[0].firstName ?? '-'}</strong>
							</Typography>
							<Typography color="primary">
								Last Name: <strong>{keycloakUsers?.data[0].lastName ?? '-'}</strong>
							</Typography>
						</Box>
					)}

					{searchEmailError && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Typography color="warning">{searchEmailError}</Typography>
							<TextField
								label="Email"
								value={email}
								onChange={e => handleSetEmail(e.target.value)}
								fullWidth
								error={!!emailError}
								helperText={emailError}
							/>
							<TextField
								label="First Name"
								value={firstName}
								onChange={e => handleSetFirstName(e.target.value)}
								fullWidth
								error={!!firstNameError}
								helperText={firstNameError}
							/>
							<TextField
								label="Last Name"
								value={lastName}
								onChange={e => handleSetLastName(e.target.value)}
								fullWidth
								error={!!lastNameError}
								helperText={lastNameError}
							/>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				{!!email && !searchEmailError && (
					<Button onClick={handleAddUser} color="primary">
						Add User
					</Button>
				)}
				{searchEmailError && (
					<Button
						onClick={handleCreateUser}
						color="primary"
						disabled={!!emailError || !!firstNameError || !!lastNameError}
					>
						Create New User
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
